import { normalizeWorkerTags, workerTagOptions, WorkerTagOption } from '@app/pages/admin/worker-tags-page/worker-tags.util';

/**
 * Process-key admission overlay helpers for the resource-profile dialog.
 */

export interface ProcessCapCatalogItem {
  key: string;
  displayName?: string | null;
}

export interface ProcessCapRow {
  key: string;
  label: string;
  capInput: string;
}

export type ParsedCapInput =
  | { kind: 'inherit' }
  | { kind: 'value'; value: number }
  | { kind: 'invalid' };

export function parseCapInput(raw: string | null | undefined): ParsedCapInput {
  const trimmed = (raw ?? '').trim();
  if (trimmed.length === 0) {
    return { kind: 'inherit' };
  }
  if (!/^\d+$/.test(trimmed)) {
    return { kind: 'invalid' };
  }
  return { kind: 'value', value: Number(trimmed) };
}

export function processCapLabel(process: ProcessCapCatalogItem): string {
  const name = process.displayName?.trim();
  return name && name.length > 0 ? name : process.key;
}

export function buildProcessCapRows(
  processes: ProcessCapCatalogItem[] | null | undefined,
  overlay: Record<string, number> | null | undefined,
): ProcessCapRow[] {
  const overlayMap = overlay ?? {};
  const seen = new Set<string>();
  const rows: ProcessCapRow[] = [];

  const catalog = [...(processes ?? [])]
    .filter(process => !!process.key?.trim())
    .sort((left, right) => left.key.localeCompare(right.key));
  for (const process of catalog) {
    const key = process.key.trim();
    seen.add(key.toLowerCase());
    rows.push({
      key,
      label: processCapLabel(process),
      capInput: overlayValueText(overlayMap, key),
    });
  }

  const extraKeys = Object.keys(overlayMap)
    .filter(key => key.trim().length > 0 && !seen.has(key.trim().toLowerCase()))
    .sort((left, right) => left.localeCompare(right));
  for (const key of extraKeys) {
    const trimmed = key.trim();
    rows.push({
      key: trimmed,
      label: trimmed,
      capInput: overlayValueText(overlayMap, trimmed),
    });
  }

  return rows;
}

export function overlayFromCapRows<T extends { key: string; capInput: string }>(
  rows: readonly T[],
): { overlay: { [key: string]: number }; invalid: boolean } {
  const overlay: { [key: string]: number } = {};
  let invalid = false;
  for (const row of rows) {
    const parsed = parseCapInput(row.capInput);
    if (parsed.kind === 'invalid') {
      invalid = true;
      continue;
    }
    if (parsed.kind === 'value') {
      overlay[row.key] = parsed.value;
    }
  }
  return { overlay, invalid };
}

export function formatProcessConcurrencySummary(
  overlay: Record<string, number> | null | undefined,
): string {
  const keys = Object.keys(overlay ?? {}).filter(key => key.trim().length > 0).sort((left, right) => left.localeCompare(right));
  if (keys.length === 0) {
    return '';
  }
  return keys.map(key => `${key}: ${(overlay ?? {})[key]}`).join(', ');
}

function overlayValueText(overlay: Record<string, number>, key: string): string {
  const match = Object.entries(overlay).find(([overlayKey]) => overlayKey.toLowerCase() === key.toLowerCase());
  return match === undefined ? '' : String(match[1]);
}

export function overlayCapInput(
  overlay: Record<string, number> | null | undefined,
  processKey: string,
): string {
  return overlayValueText(overlay ?? {}, processKey);
}

export function applyCapToOverlay(
  overlay: Record<string, number> | null | undefined,
  processKey: string,
  capInput: string,
): { overlay: { [key: string]: number }; invalid: boolean } {
  const parsed = parseCapInput(capInput);
  const key = processKey.trim();
  const next: { [key: string]: number } = {};
  for (const [overlayKey, value] of Object.entries(overlay ?? {})) {
    if (overlayKey.trim().toLowerCase() === key.toLowerCase()) {
      continue;
    }
    next[overlayKey] = value;
  }
  if (parsed.kind === 'invalid') {
    return { overlay: next, invalid: true };
  }
  if (parsed.kind === 'value' && key.length > 0) {
    next[key] = parsed.value;
  }
  return { overlay: next, invalid: false };
}

/** A field that is only this token stores an empty tag list (any worker). */
export const ANY_WORKER_TAG_INPUT = '-';

export interface ProfileWorkerTagSource {
  requiredWorkerTags?: { [key: string]: string[] | null } | null;
}

/** TODO: migrate to api-client when OrchestrationResourceProfileResponse declares requiredWorkerTags. */
export function readProfileWorkerTags(
  profile: object | null | undefined,
): { [key: string]: string[] } {
  const raw = (profile as ProfileWorkerTagSource | null | undefined)?.requiredWorkerTags ?? {};
  const tags: { [key: string]: string[] } = {};
  for (const [key, value] of Object.entries(raw)) {
    const trimmed = key.trim();
    if (!trimmed) {
      continue;
    }
    tags[trimmed] = (value ?? []).map(tag => tag.trim()).filter(tag => tag.length > 0);
  }
  return tags;
}

export interface ProcessOverlayRow {
  key: string;
  label: string;
  capInput: string;
  selectedTags: string[];
  anyWorker: boolean;
  tagOptions: WorkerTagOption[];
}

/** One catalog row with the admission cap and the worker tags side by side. */
export function buildProcessOverlayRows(
  processes: ProcessCapCatalogItem[] | null | undefined,
  caps: Record<string, number> | null | undefined,
  tags: { [key: string]: string[] } | null | undefined,
  catalogTags: readonly string[] = [],
): ProcessOverlayRow[] {
  const capMap = caps ?? {};
  const tagMap = tags ?? {};
  const seen = new Set<string>();
  const rows: ProcessOverlayRow[] = [];
  const catalog = [...(processes ?? [])]
    .filter(process => !!process.key?.trim())
    .sort((left, right) => left.key.localeCompare(right.key));
  for (const process of catalog) {
    const key = process.key.trim();
    seen.add(key.toLowerCase());
    rows.push(overlayRow(key, processCapLabel(process), overlayValueText(capMap, key), tagMap, catalogTags));
  }
  const extraKeys = new Map<string, string>();
  for (const key of [...Object.keys(capMap), ...Object.keys(tagMap)]) {
    const trimmed = key.trim();
    const folded = trimmed.toLowerCase();
    if (trimmed.length === 0 || seen.has(folded) || extraKeys.has(folded)) {
      continue;
    }
    extraKeys.set(folded, trimmed);
  }
  for (const key of [...extraKeys.values()].sort((left, right) => left.localeCompare(right))) {
    rows.push(overlayRow(key, key, overlayValueText(capMap, key), tagMap, catalogTags));
  }
  return rows;
}

export function overlayFromTagRows<T extends { key: string; selectedTags: readonly string[]; anyWorker: boolean }>(
  rows: readonly T[],
): { [key: string]: string[] } {
  const overlay: { [key: string]: string[] } = {};
  for (const row of rows) {
    const key = row.key.trim();
    if (!key) {
      continue;
    }
    if (row.anyWorker) {
      overlay[key] = [];
      continue;
    }
    const selected = normalizeWorkerTags(row.selectedTags);
    if (selected.length === 0) {
      continue;
    }
    overlay[key] = selected;
  }
  return overlay;
}

export function applyTagsToOverlay(
  overlay: { [key: string]: string[] } | null | undefined,
  processKey: string,
  selectedTags: readonly string[],
  anyWorker: boolean,
): { [key: string]: string[] } {
  const key = processKey.trim();
  const next: { [key: string]: string[] } = {};
  for (const [overlayKey, value] of Object.entries(overlay ?? {})) {
    if (overlayKey.trim().toLowerCase() === key.toLowerCase()) {
      continue;
    }
    next[overlayKey] = value;
  }
  if (!key) {
    return next;
  }
  if (anyWorker) {
    next[key] = [];
    return next;
  }
  const selected = normalizeWorkerTags(selectedTags);
  if (selected.length > 0) {
    next[key] = selected;
  }
  return next;
}

export function formatWorkerTagSummary(
  overlay: { [key: string]: string[] } | null | undefined,
): string {
  const keys = Object.keys(overlay ?? {})
    .filter(key => key.trim().length > 0)
    .sort((left, right) => left.localeCompare(right));
  if (keys.length === 0) {
    return '';
  }
  return keys.map(key => {
    const tags = (overlay ?? {})[key] ?? [];
    return `${key}: ${tags.length === 0 ? ANY_WORKER_TAG_INPUT : tags.join(', ')}`;
  }).join(', ');
}

export function tagSelectionForProcess(
  overlay: { [key: string]: string[] } | null | undefined,
  processKey: string,
): { selectedTags: string[]; anyWorker: boolean } {
  const match = Object.entries(overlay ?? {}).find(
    ([overlayKey]) => overlayKey.trim().toLowerCase() === processKey.trim().toLowerCase(),
  );
  if (!match) {
    return { selectedTags: [], anyWorker: false };
  }
  if (match[1].length === 0) {
    return { selectedTags: [], anyWorker: true };
  }
  return { selectedTags: [...match[1]], anyWorker: false };
}

function overlayRow(
  key: string,
  label: string,
  capInput: string,
  tags: { [key: string]: string[] },
  catalogTags: readonly string[],
): ProcessOverlayRow {
  const selection = tagSelectionForProcess(tags, key);
  return {
    key,
    label,
    capInput,
    selectedTags: selection.selectedTags,
    anyWorker: selection.anyWorker,
    tagOptions: workerTagOptions(catalogTags, selection.selectedTags),
  };
}

/** Maps a failed profile save to an i18n key or the server validation text. */
export function resourceProfileSaveError(error: unknown): string {
  const messages = validationMessages(error);
  if (messages.some(message => message.toLowerCase().includes('already exists'))) {
    return 'A resource profile with this key already exists';
  }
  return messages[0] ?? 'Failed to save resource profile';
}

function validationMessages(error: unknown): string[] {
  const body = (error as { error?: { errors?: Record<string, unknown> } } | null)?.error;
  const errors = body?.errors;
  if (!errors) {
    return [];
  }
  const messages: string[] = [];
  for (const value of Object.values(errors)) {
    const list = Array.isArray(value) ? value : [value];
    for (const item of list) {
      if (typeof item === 'string' && item.trim().length > 0) {
        messages.push(item.trim());
      }
    }
  }
  return messages;
}

export const SATELLITE_RESOURCE_WORKER_GROUP = 'satellite';

export function findActiveSatelliteProfile<T extends { workerGroup?: string | null; isActive?: boolean }>(
  profiles: T[] | null | undefined,
): T | null {
  return (profiles ?? []).find(profile =>
    profile.isActive === true
    && (profile.workerGroup ?? '').trim().toLowerCase() === SATELLITE_RESOURCE_WORKER_GROUP
  ) ?? null;
}
